import { ArrowDownRight, ArrowUpRight } from 'lucide-react'

import {
  Card,
  CardContent,
  CardHeading,
  FigureValue,
  LabeledSwatch,
} from '@/components/ui'
import { chartColors, cn, numberToCurrency } from '@/utils'
import { type ValuePoint, ValueTrendChart } from './ValueTrendChart'

interface MarketValueHeroProps {
  marketValue: number
  // Null when the statements printed no cost basis and no gain, so no gain can be claimed.
  unrealized: number | null
  unrealizedPercent: number | null
  // Null when a holding printed no cost basis, which also hides the snapshot bars.
  costBasis: number | null
  asOfLabel: string
  // Total market value per statement period, oldest first. Draws the trend line; one point (or none)
  // hides it.
  valuePoints: ValuePoint[]
  // The period the snapshot selector is on, so the chart highlights the same point the headline
  // reports. Undefined means latest snapshot (the newest point).
  selectedKey?: string
}

const signed = (value: number) => (value >= 0 ? '+' : '')

// A green, red, or neutral pill so the gain or loss reads before the number does.
const returnChipTone = (value: number) => {
  if (value > 0) return 'bg-emerald-50 text-emerald-700'
  if (value < 0) return 'bg-red-50 text-red-700'
  return 'bg-blue-1 text-blue-6'
}

// For a single snapshot a trend line is just one dot, so show that month's two numbers instead:
// market value against cost basis. The gap between the bars is the unrealized gain or loss.
const SnapshotBars = ({
  marketValue,
  costBasis,
}: {
  marketValue: number
  costBasis: number
}) => {
  const largest = Math.max(marketValue, costBasis, 1)
  const bars = [
    { label: 'Market value', value: marketValue, color: chartColors.blue },
    { label: 'Cost basis', value: costBasis, color: chartColors.gray },
  ]

  return (
    <div className="flex flex-col gap-3.5">
      {bars.map((bar) => (
        <div key={bar.label} className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between text-xs">
            <LabeledSwatch
              color={bar.color}
              label={bar.label}
              className="text-ink-subtle"
            />
            <span className="font-semibold tabular-nums text-foreground">
              {numberToCurrency(bar.value)}
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${(bar.value / largest) * 100}%`,
                backgroundColor: bar.color,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

// The headline: how much is in the book now, how much is gain, and the shape of how it got there.
export const MarketValueHero = ({
  marketValue,
  unrealized,
  unrealizedPercent,
  costBasis,
  asOfLabel,
  valuePoints,
  selectedKey,
}: MarketValueHeroProps) => {
  const ReturnArrow = (unrealized ?? 0) >= 0 ? ArrowUpRight : ArrowDownRight

  return (
    <Card className="flex flex-col">
      <CardHeading
        className="flex-wrap"
        title="Market value"
        actions={
          <span className="text-xs text-ink-subtle">as of {asOfLabel}</span>
        }
      />
      <CardContent className="flex flex-1 flex-col gap-4">
        <div>
          <FigureValue className="text-3xl">
            {numberToCurrency(marketValue)}
          </FigureValue>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            {unrealized == null ? (
              <span className="inline-flex items-center rounded-full bg-blue-1 px-2 py-0.5 font-medium text-ink-subtle">
                —
              </span>
            ) : (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-medium tabular-nums',
                  returnChipTone(unrealized),
                )}
              >
                <ReturnArrow className="h-3.5 w-3.5" />
                {signed(unrealized)}
                {numberToCurrency(Math.abs(unrealized))}
                {unrealizedPercent != null &&
                  ` (${signed(unrealized)}${unrealizedPercent.toFixed(1)}%)`}
              </span>
            )}
            <span className="text-ink-subtle">unrealized gain / loss</span>
          </div>
        </div>

        <div className="mt-auto">
          {selectedKey && costBasis != null ? (
            <SnapshotBars marketValue={marketValue} costBasis={costBasis} />
          ) : (
            <ValueTrendChart points={valuePoints} />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
